import { Injectable, Component, EventEmitter, Output, Input } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { CollageSetService } from '../../services/collage-set.service';
import { AuthService } from '../../services/auth.service';

// ── Global service to open/close dialog from anywhere ──
@Injectable({ providedIn: 'root' })
export class UploadDialogService {
  private _open$ = new BehaviorSubject<{ frameIndex: number; frameLabel: string } | null>(null);
  open$ = this._open$.asObservable();

  private _result$ = new BehaviorSubject<{ frameIndex: number; url: string } | null>(null);
  result$ = this._result$.asObservable();

  open(frameIndex: number, frameLabel: string = '') {
    this._open$.next({ frameIndex, frameLabel: frameLabel || 'Frame ' + (frameIndex + 1) });
  }

  close() {
    this._open$.next(null);
  }

  emitResult(frameIndex: number, url: string) {
    this._result$.next({ frameIndex, url });
  }
}

// ── Upload Dialog Component ──
@Component({
  selector: 'app-upload-dialog',
  templateUrl: './upload-dialog.component.html'
})
export class UploadDialogComponent {
  isOpen = false;
  frameIndex = 0;
  frameLabel = '';

  uploadingFiles: { name: string; path: string; image: string }[] = [];
  isUploading = false;
  uploadError = '';
  myPhotos: string[] = [];

  constructor(
    public dialogService: UploadDialogService,
    private collageService: CollageSetService,
    private auth: AuthService
  ) {
    // Load saved photos
    try { this.myPhotos = JSON.parse(localStorage.getItem('my_photos') || '[]'); } catch { this.myPhotos = []; }

    // Listen for open/close
    this.dialogService.open$.subscribe(data => {
      if (data) {
        this.isOpen = true;
        this.frameIndex = data.frameIndex;
        this.frameLabel = data.frameLabel;
        this.uploadingFiles = [];
        this.uploadError = '';
        this.isUploading = false;
        // Refresh photos
        try { this.myPhotos = JSON.parse(localStorage.getItem('my_photos') || '[]'); } catch { this.myPhotos = []; }
      } else {
        this.isOpen = false;
      }
    });
  }

  close() {
    this.dialogService.close();
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;

    Array.from(input.files).forEach(file => {
      if (!file.type.includes('jpeg') && !file.type.includes('jpg')) {
        this.uploadError = 'Only JPG/JPEG files are allowed.';
        return;
      }
      this.uploadError = '';
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const img = new Image();
        img.src = reader.result as string;
        img.onload = () => {
          const w = img.width, h = img.height;
          if ((w >= h && (w < 750 || h < 450)) || (h > w && (h < 750 || w < 450)) || (w === h && w < 750)) {
            this.uploadError = 'Image must be at least 750x450px for quality print.';
            return;
          }
          this.uploadingFiles.push({ name: file.name, path: reader.result as string, image: reader.result as string });
        };
      };
    });
    input.value = '';
  }

  removeUploadFile(index: number) {
    this.uploadingFiles.splice(index, 1);
  }

  uploadPhotos() {
    if (this.uploadingFiles.length === 0) return;
    this.isUploading = true;
    const userId = this.auth.currentUser?.id || 0;
    let completed = 0;

    this.uploadingFiles.forEach(file => {
      this.collageService.uploadMedia(file.name, file.image, userId).subscribe({
        next: (res: any) => {
          const url = res?.data?.url || res?.url || '';
          if (url) {
            this.myPhotos.push(url);
            localStorage.setItem('my_photos', JSON.stringify(this.myPhotos));
            this.dialogService.emitResult(this.frameIndex, url);
          }
          completed++;
          if (completed >= this.uploadingFiles.length) {
            this.isUploading = false;
            this.close();
          }
        },
        error: () => {
          completed++;
          if (completed >= this.uploadingFiles.length) {
            this.isUploading = false;
            this.uploadError = 'Upload failed. Please try again.';
          }
        }
      });
    });
  }

  useExistingPhoto(photoUrl: string) {
    this.dialogService.emitResult(this.frameIndex, photoUrl);
    this.close();
  }

  removePhoto(index: number, event: Event) {
    event.stopPropagation();
    this.myPhotos.splice(index, 1);
    localStorage.setItem('my_photos', JSON.stringify(this.myPhotos));
  }
}
